import { useLocale as useLocaleR42 } from '@/lib/locale';
import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell } from 'recharts';
import { TrendingUp, ArrowUpRight, ArrowDownRight, MapPin, Wallet, Users, Sparkles } from 'lucide-react';

/* ------------------------------------------------------------------
 * HeroDashboardMock — the landing hero visual (owner ask, Sept 2026).
 *
 * Sells the OWNER view of a small swim school: whole-operation revenue
 * by month, lifetime value per family, per-location month-over-month
 * trends and a payroll-ready chip. Static sample data (clearly a demo
 * school), styled like the real dashboard so it reads as the product.
 * ------------------------------------------------------------------ */

// Year-to-date only (Jan–Sep) so the "year to date" total is honest and the
// current month reads as the peak — a growth story, not a summer slump.
const MONTHS = [
    { m: 'Jan', v: 9800 }, { m: 'Feb', v: 10400 }, { m: 'Mar', v: 11900 }, { m: 'Apr', v: 12600 },
    { m: 'May', v: 13100 }, { m: 'Jun', v: 15800 }, { m: 'Jul', v: 16900 }, { m: 'Aug', v: 17400 },
    { m: 'Sep', v: 19400 },
];
const CURRENT_IDX = MONTHS.length - 1; // September highlighted

const FAMILIES = [
    { name: 'The Nguyen family', kids: 2, ltv: 4860 },
    { name: 'The Patel family', kids: 3, ltv: 4120 },
    { name: 'The Rivera family', kids: 1, ltv: 2790 },
];

const LOCATIONS = [
    { name: 'Main pool', rev: 12640, pct: 0.14, up: true },
    { name: 'Northside Rec', rev: 6760, pct: 0.08, up: true },
];

const money = (n) => `$${n.toLocaleString()}`;
const compact = (n) => (n >= 1000 ? `$${Math.round(n / 1000)}k` : `$${n}`);

export function HeroDashboardMock() {
    const { t: t42 } = useLocaleR42();
    const total = MONTHS.reduce((a, b) => a + b.v, 0);
    const cur = MONTHS[CURRENT_IDX].v;
    const prev = MONTHS[CURRENT_IDX - 1].v;
    const maxLtv = Math.max(...FAMILIES.map((f) => f.ltv));

    return (
        <Card className="relative w-full min-w-0 max-w-full overflow-hidden rounded-3xl border-0 bg-white shadow-[0_30px_70px_rgba(10,22,40,0.18)]" data-testid="landing-hero-dashboard-mock">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 border-b bg-[#0a1628] px-5 py-3">
                <span className="h-2.5 w-2.5 rounded-full bg-[#ff6b35]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#ffd700]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#00d4ff]" />
                <span className="ml-3 truncate text-xs text-white/60">{t42("splashpad.pro · Superhero Swim Academy · Owner dashboard")}</span>
                <Badge className="ml-auto hidden bg-[#ffd700] px-1.5 py-0 text-[9px] font-bold text-[#0a1628] hover:bg-[#ffd700] sm:inline-flex">
                    <Sparkles className="mr-0.5 h-2.5 w-2.5" /> {t42("PRO")} </Badge>
            </div>

            <div className="min-w-0 space-y-3 p-4 sm:p-5">
                {/* KPI strip */}
                <div className="grid min-w-0 grid-cols-3 gap-2 sm:gap-3">
                    <Kpi label={t42("Revenue · Sept")} value={money(cur)} sub={`+${Math.round(((cur - prev) / prev) * 100)}% vs Aug`} up={cur >= prev} accent="#00d4ff" testid="hero-mock-kpi-revenue" />
                    <Kpi label={t42("Active students")} value="132" sub="across 2 pools" accent="#ff6b35" icon={Users} testid="hero-mock-kpi-students" />
                    <Kpi label={t42("Staff hours · Sept")} value="212 h" sub="payroll ready" accent="#ffd700" icon={Wallet} testid="hero-mock-kpi-hours" />
                </div>

                {/* Revenue chart — whole operation */}
                <div className="min-w-0 rounded-2xl border border-[#0a1628]/10 bg-white p-3 sm:p-4" data-testid="hero-mock-revenue-chart">
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#1a3a5c]/70">{t42("Revenue · all locations · 2026")}</div>
                            <div className="font-display text-xl font-bold text-[#0a1628] sm:text-2xl">{money(total)} <span className="text-xs font-normal text-[#1a3a5c]/60">{t42("year to date")}</span></div>
                        </div>
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#00b87a]/15 px-2 py-0.5 text-[10px] font-bold text-[#00b87a]">
                            <TrendingUp className="h-3 w-3" /> {t42("+31% vs 2025")} </span>
                    </div>
                    <div className="mt-2 h-[120px] w-full min-w-0 max-w-full overflow-hidden sm:h-[140px]" aria-hidden="true">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={MONTHS} margin={{ top: 6, right: 0, left: -22, bottom: 0 }} barCategoryGap="28%">
                                <CartesianGrid stroke="#e5edf7" strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="m" tick={{ fontSize: 10, fill: '#1a3a5c' }} axisLine={false} tickLine={false} interval={0} />
                                <YAxis tick={{ fontSize: 10, fill: '#1a3a5c' }} axisLine={false} tickLine={false} tickFormatter={compact} width={48} />
                                <Bar dataKey="v" radius={[5, 5, 0, 0]} isAnimationActive={false}>
                                    {MONTHS.map((_, i) => (
                                        <Cell key={i} fill={i === CURRENT_IDX ? '#0a1628' : '#00d4ff'} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Bottom row: LTV + locations */}
                <div className="grid min-w-0 gap-3 sm:grid-cols-2">
                    <div className="min-w-0 rounded-2xl border border-[#0a1628]/10 bg-[#f4f7fb] p-3 sm:p-4" data-testid="hero-mock-ltv">
                        <div className="flex items-center justify-between">
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#1a3a5c]/70">{t42("Lifetime value · top families")}</div>
                            <Sparkles className="h-3.5 w-3.5 text-[#ffd700]" aria-hidden="true" />
                        </div>
                        <ul className="mt-2 space-y-2">
                            {FAMILIES.map((f) => (
                                <li key={f.name}>
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="truncate font-semibold text-[#0a1628]">{f.name} <span className="font-normal text-[#1a3a5c]/60">· {f.kids} {t42("swimmer")}{f.kids > 1 ? 's' : ''}</span></span>
                                        <span className="shrink-0 tabular-nums font-bold text-[#0a1628]">{money(f.ltv)}</span>
                                    </div>
                                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[#0a1628]/8" aria-hidden="true">
                                        <div className="h-full rounded-full bg-[#ffd700]" style={{ width: `${Math.round((f.ltv / maxLtv) * 100)}%` }} />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="min-w-0 rounded-2xl border border-[#0a1628]/10 bg-[#f4f7fb] p-3 sm:p-4" data-testid="hero-mock-locations">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#1a3a5c]/70">{t42("By location · vs last month")}</div>
                        <ul className="mt-2 space-y-2">
                            {LOCATIONS.map((l) => (
                                <li key={l.name} className="flex items-center justify-between rounded-xl bg-white px-3 py-2">
                                    <span className="flex items-center gap-1.5 text-xs font-semibold text-[#0a1628]"><MapPin className="h-3.5 w-3.5 text-[#00d4ff]" aria-hidden="true" /> {l.name}</span>
                                    <span className="flex items-center gap-1.5 text-xs">
                                        <span className="tabular-nums font-bold text-[#0a1628]">{money(l.rev)}</span>
                                        <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${l.up ? 'bg-[#00b87a]/15 text-[#00b87a]' : 'bg-[#ff6b35]/15 text-[#ff6b35]'}`}>
                                            {l.up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />} {Math.round(l.pct * 100)}%
                                        </span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-2 flex items-center justify-between rounded-xl bg-[#0a1628] px-3 py-2 text-[11px] text-white">
                            <span className="flex shrink-0 items-center gap-1.5"><Wallet className="h-3.5 w-3.5 text-[#ffd700]" aria-hidden="true" /> {t42("September payroll")}</span>
                            <span className="truncate font-bold">{t42("$4,660 · sent to accountant")}</span>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
}

function Kpi({ label, value, sub, up, accent, icon: Icon, testid }) {
    return (
        <div className="min-w-0 rounded-2xl border border-[#0a1628]/10 bg-white p-2.5 sm:p-3" style={{ borderTop: `3px solid ${accent}` }} data-testid={testid}>
            <div className="truncate text-[9px] font-semibold uppercase tracking-wider text-[#1a3a5c]/70 sm:text-[10px]">{label}</div>
            <div className="mt-0.5 font-display text-base font-bold text-[#0a1628] sm:text-xl">{value}</div>
            <div className={`mt-0.5 flex items-center gap-1 text-[10px] ${up ? 'font-semibold text-[#00b87a]' : 'text-[#1a3a5c]/70'}`}>
                {up ? <ArrowUpRight className="h-3 w-3" /> : Icon ? <Icon className="h-3 w-3" /> : null}
                <span className="truncate">{sub}</span>
            </div>
        </div>
    );
}

export default HeroDashboardMock;
